import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { UninstallShopifyIntegrationModal } from "@v2/features/settings/shop/modals/UninstallShopifyIntegrationModal";
import { Link2Off } from "lucide-react";

export const ShopifyIntegration: React.FC<{
  project: ReploProject;
}> = ({ project }) => {
  const shopifyData = project?.integrations?.shopify?.store;
  const [isUninstallModalOpen, setIsUninstallModalOpen] = React.useState(false);

  if (shopifyData) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="p-3 border-0.5 border-border rounded-xl w-fit">
            <img
              src="/images/integration-logos/shopify.png"
              alt="Shopify"
              className="w-5 h-5"
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="header-h6">Shopify</span>
            <span className="text-xs text-muted">
              Replo pulls your theme, products, and store info from the store
              below.
            </span>
          </div>
        </div>
        <div className="flex bg-sheet items-center justify-between p-3 border-0.5 rounded-lg border-border">
          <span className="text-sm font-medium">
            {project.integrations?.shopify?.store?.shopifyUrl}
          </span>
          <div className="flex items-center gap-2">
            <Tooltip content="Disconnect Shopify Store" triggerAsChild>
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => {
                  setIsUninstallModalOpen(true);
                }}
              >
                <Link2Off />
              </Button>
            </Tooltip>
            <Tag variant="success" size="base">
              <img
                src="/images/shopify-logo.svg"
                alt="Shopify logo"
                className="w-4 h-4"
              />
              Connected
            </Tag>
          </div>
        </div>
        <UninstallShopifyIntegrationModal
          workspaceId={project.ownerWorkspaceId}
          integrationId={shopifyData.integrationId ?? ""}
          open={isUninstallModalOpen}
          onOpenChange={setIsUninstallModalOpen}
        />
      </div>
    );
  }

  return (
    <ConnectShopifyCallout
      cookieValue={{
        type: "shopSettingsIntegration",
        projectId: project.id,
        workspaceId: project.ownerWorkspaceId,
      }}
    />
  );
};
